import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

type OutageSeverity = 'info' | 'warning' | 'error';

interface RemoteOutageConfig {
  enabled?: boolean;
  severity?: OutageSeverity;
  message?: string;
  updatedAt?: string; // ISO timestamp
}

/**
 * Maintains site-wide outage banner state & severity. Supports remote polling of a JSON config
 * hosted on S3/CloudFront to allow toggling without redeploy.
 */
@Injectable({ providedIn: 'root' })
export class OutageStatusService {
  private readonly defaultMessage = 'This site is currently unavailable due to a temporary outage. We are monitoring the situation and will restore service as soon as possible. Thank you for your patience.';

  private readonly _outage$ = new BehaviorSubject<boolean>(!!(environment as any).forceOutageBanner);
  private readonly _severity$ = new BehaviorSubject<OutageSeverity>((environment as any).outageBannerSeverity || 'error');
  private readonly _message$ = new BehaviorSubject<string>(this.defaultMessage);

  private readonly pollingInterval = 60000; // 60 seconds

  readonly outage$: Observable<boolean> = this._outage$.asObservable();
  readonly severity$: Observable<OutageSeverity> = this._severity$.asObservable();
  readonly message$: Observable<string> = this._message$.asObservable();

  private pollingSub?: Subscription;
  private consecutiveFailures = 0;

  /** Derived banner type for Forge component */
  get bannerType(): OutageSeverity { return this._severity$.value; }
  get outageMessage(): string { return this._message$.value; }

  setOutage(isOut: boolean): void { this._outage$.next(isOut); }
  setSeverity(severity: OutageSeverity): void { this._severity$.next(severity); }
  setMessage(message: string): void { this._message$.next(message || this.defaultMessage); }

  get isOutage(): boolean { return this._outage$.value; }

  /** Start polling remote config if URL provided. */
  startRemotePolling(pollMs: number = this.pollingInterval): void {
    const url = (environment as any).outageBannerConfigUrl;
    if (!url) return;

    // Immediately attempt first fetch, then on interval
    this.fetchAndApplyRemote(url);

    this.pollingSub?.unsubscribe();
    this.pollingSub = timer(pollMs, pollMs).subscribe(() => this.fetchAndApplyRemote(url));
  }

  stopRemotePolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private async fetchAndApplyRemote(url: string): Promise<void> {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json: RemoteOutageConfig = await response.json();

      this.consecutiveFailures = 0;

      if (typeof json.enabled === 'boolean') {
        this.setOutage(json.enabled);
      }

      if (json.enabled) {
        if (json.severity && ['info', 'warning', 'error'].includes(json.severity)) {
          this.setSeverity(json.severity);
        }
        if (json.message && json.message.trim().length > 0) {
          this.setMessage(json.message.trim());
        } else {
          this.setMessage(this.defaultMessage);
        }
        // Optionally append last updated info if provided
        if (json.updatedAt) {
          const updated = new Date(json.updatedAt);
          if (!isNaN(updated.getTime())) {
            const stamp = updated.toISOString().replace('T', ' ').substring(0, 16) + ' UTC';
            this.setMessage(`${this.outageMessage} (Status updated ${stamp})`);
          }
        }
      } else {
        // If disabled, clear message back to default (kept for future quick enable) and hide
        this.setMessage(this.defaultMessage);
      }
    } catch (err) {
      this.consecutiveFailures++;
      // After several failures optionally stop polling or backoff (simplified here)
      if (this.consecutiveFailures >= 10) {
        this.stopRemotePolling();
      }
    }
  }
}